import { ApolloError } from '@apollo/client';
import {
  LanguageId,
  useArticleTranslationVersionsQuery,
} from 'lib-knowledge-management-common/graphql';

export type Version = {
  versionNumber: number;
  publishedAt: number;
  publishedBy: string;
  title: string;
  isCurrentlyIndexed: boolean;
  /**
   * Whether AI helped draft this version. The API reports `null` for an unknown
   * origin, including versions published before the field existed; both are
   * flattened to `false` since the UI only marks the positive case.
   */
  aiAssisted: boolean;
  /**
   * The conversations this version was drafted from, in the shape the shared
   * chip and the app's renderer take. Empty for versions that reference none —
   * today that is every version except the one AI originally drafted, since
   * nothing writes provenance on update.
   */
  referencedConversations: readonly { csid: number }[];
};

export function useVersions({
  articleId,
  languageId,
}: {
  articleId: string;
  languageId: LanguageId;
}): { versions: Version[]; loading: boolean; error: ApolloError | undefined } {
  const { data, loading, error } = useArticleTranslationVersionsQuery({
    variables: { articleId, languageId },
    fetchPolicy: 'cache-and-network',
  });

  const versions =
    data?.article?.translations.versions
      .filter((v) => v.version.__typename === 'Indexed')
      .map((v) => ({
        versionNumber:
          v.version.__typename === 'Indexed' ? v.version.versionNumber : 0,
        publishedAt: v.publishedAt,
        publishedBy: v.publishedBy?.name ?? 'Unknown user',
        title: v.title,
        isCurrentlyIndexed: v.isCurrentlyIndexed,
        aiAssisted: !!v.aiAssisted,
        referencedConversations: (v.referencedConversationIds ?? []).map(
          (csid) => ({ csid }),
        ),
      })) ?? [];

  return { versions, loading, error };
}
